import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Twitter, Facebook, Youtube, MessageCircle, Music, Menu, X } from 'lucide-react';
import DotPanel from './DotPanel';
import BackgroundLines from './BackgroundLines';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const slide = {
    title: "Hızlı. Tarafsız. Güvenilir.",
    description: "Türkiye'nin ilk dijital haber platformuna şimdi kayıt olun, erken erişim fırsatlarını kaçırmayın."
  };

  const socialIcons = [
    { icon: Instagram, href: "#" },
    { icon: Twitter, href: "#" },
    { icon: Facebook, href: "#" },
    { icon: Youtube, href: "#" },
    { icon: MessageCircle, href: "#" },
    { icon: Music, href: "#" }
  ];

  const menuItems = [
    "Hakkımızda",
    "İletişim", 
    "Politikalar",
    "Künye"
  ];

  return (
    <div className="landing-page">
      <BackgroundLines />
      
      <motion.header 
        className="header"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="logo">Rixino Haber</div>
        <div className="social-icons">
          {socialIcons.map((social, index) => (
            <motion.a
              key={index}
              href={social.href}
              className="social-icon"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 200
              }}
              whileHover={{ scale: 1.2 }}
            >
              <social.icon size={20} />
            </motion.a>
          ))}
        </div>
        <motion.button
          className="burger-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </motion.header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="burger-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsMenuOpen(false)}
          >
            <motion.div
              className="burger-menu-content"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="menu-section">
                <h3>Menü</h3>
                {menuItems.map((item: string, index: number) => (
                  <motion.a
                    key={index}
                    href="#"
                    className="burger-menu-item"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ 
                      duration: 0.4, 
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ x: 10 }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item}
                  </motion.a>
                ))}
              </div>
              
              <div className="menu-section">
                <h3>Sosyal Medya</h3>
                <div className="burger-social-icons">
                  {socialIcons.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      className="burger-social-icon"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: 0.4 + index * 0.1,
                        type: "spring",
                        stiffness: 200
                      }}
                      whileHover={{ scale: 1.2 }}
                    >
                      <social.icon size={24} />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.nav 
        className="left-menu"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        {menuItems.map((item: string, index: number) => (
          <motion.a
            key={index}
            href="#"
            className="menu-item"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 0.6, 
              delay: 0.5 + index * 0.1,
              type: "spring",
              stiffness: 100
            }}
            whileHover={{ x: 10 }}
          >
            {item}
          </motion.a>
        ))}
      </motion.nav>

      <main className="main-content">
        <motion.div 
          className="content-container"
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <DotPanel />
          
          <div className="content-area">
            <div className="slide-content">
              <motion.h1 
                className="slide-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {slide.title}
              </motion.h1>
              <motion.p 
                className="slide-description"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {slide.description}
              </motion.p>
              
              <motion.form 
                className="signup-form"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="Kullanıcı adınız" 
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    placeholder="E-posta adresiniz" 
                    className="form-input"
                  />
                </div>
                <motion.button 
                  type="submit" 
                  className="submit-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  İleri
                </motion.button>
              </motion.form>
            </div>
          </div>
        </motion.div>
      </main>

      <motion.footer 
        className="footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
      >
        <p>© 2025 - Rixino Haber</p>
      </motion.footer>
    </div>
  );
};

export default LandingPage;
